import React, { createContext, useContext, useState, useEffect } from 'react';
import authAPI from '../services/auth';
import resumeAPI from '../services/api';
import { persistentStorage } from '../services/persistentStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const protectedEmail = 'benjamin.maderajr@gmail.com';
  const isProtected = (email) => (email || '').trim().toLowerCase() === protectedEmail;

  useEffect(() => {
    // ALWAYS try to restore from backup first (multiple locations)
    const PERSISTENT_KEY = 'benjamin_madera_persistent_backup';
    const BACKUP_KEY = 'BACKUP_benjamin_madera';
    
    const backupStr = localStorage.getItem(PERSISTENT_KEY) || localStorage.getItem(BACKUP_KEY);
    if (backupStr) {
      try {
        const backup = JSON.parse(backupStr);
        console.log('[AuthContext] Auto-restoring from backup...');
        
        // Restore user session
        localStorage.setItem('user', JSON.stringify(backup.user));
        
        // Restore user ID mapping
        const userIdByEmail = {};
        userIdByEmail[backup.user.email.toLowerCase()] = backup.user.user_id;
        localStorage.setItem('userIdByEmail', JSON.stringify(userIdByEmail));
        
        // Restore resume
        localStorage.setItem(`resumes_${backup.user.user_id}`, JSON.stringify([backup.resume]));
        
        // Restore local users for offline login
        localStorage.setItem('localUsers', JSON.stringify([backup.user]));
        
        console.log('[AuthContext] ✅ Data auto-restored');
      } catch (e) {
        console.error('[AuthContext] Failed to restore backup:', e);
      }
    }
    
    // Check if user is already logged in
    const currentUser = authAPI.getCurrentUser();
    setUser(currentUser);
    // Per-user migration/recovery on app load (safe, non-destructive)
    if (currentUser?.user_id) {
      try {
        if (isProtected(currentUser?.email)) {
          // Explicitly do not run automated writes for protected account
          setLoading(false);
          return;
        }
        // All legacy migration/recovery functions removed per user request
      } catch (e) {
        // don't block app load
        console.warn('Resume initialization skipped:', e);
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const userData = await authAPI.login(email, password);
      setUser(userData);
      // Per-user, non-destructive migration/recovery hook (no deletes)
      if (userData?.user_id) {
        if (isProtected(userData?.email)) {
          return userData;
        }
        const hasAwsBackend = !!(import.meta?.env?.VITE_API_BASE_URL || '').trim();
        const hasAwsTokens = !!userData?.tokens?.idToken;
        
        // If logged in with AWS, pull data FROM AWS to local storage
        if (hasAwsBackend && hasAwsTokens) {
          try {
            console.log('[AuthContext] Pulling data from AWS...');
            const profileRes = await fetch('https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev/me/profile', {
              headers: { 'Authorization': 'Bearer ' + userData.tokens.idToken }
            });
            if (profileRes.ok) {
              const profileData = await profileRes.json();
              const profile = profileData.profile;
              if (profile && Object.keys(profile).length > 0) {
                // Save profile as a resume so it shows up in the app
                const resume = {
                  id: 'resume_' + Date.now(),
                  user_id: userData.user_id,
                  owner_user_id: userData.user_id,
                  title: 'My Resume',
                  contact: {
                    full_name: profile.name,
                    email: profile.email,
                    phone: profile.phone || '',
                    location: profile.location || ''
                  },
                  ...profile,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                };
                localStorage.setItem(`resumes_${userData.user_id}`, JSON.stringify([resume]));
                
                // SAVE TO PERSISTENT STORAGE (survives logout!)
                persistentStorage.save({
                  user: {
                    user_id: userData.user_id,
                    email: userData.email,
                    name: userData.name,
                    tokens: userData.tokens
                  },
                  resume: resume
                });
                
                console.log('[AuthContext] ✓ Profile data synced from AWS and saved permanently');
              }
            }
          } catch (e) {
            console.warn('[AuthContext] Failed to pull data from AWS (non-fatal):', e);
          }
        }
        
        // Auto-sync TO AWS if user has local data but no AWS tokens
        // This ensures new accounts automatically upload their data to AWS
        const hasLocalData = false; // Recovery functions removed
        const needsSync = hasAwsBackend && hasLocalData && !hasAwsTokens;
        
        if (needsSync) {
          try {
            console.log('[AuthContext] Auto-syncing local data to AWS...');
            await resumeAPI.syncLocalDataToAWS?.();
            console.log('[AuthContext] Auto-sync complete');
          } catch (e) {
            console.warn('[AuthContext] Auto-sync failed (non-fatal):', e);
            // Don't block login if sync fails
          }
        }
      }
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const newUser = await authAPI.register(userData);
      // Auto-login after registration
      const loginData = await authAPI.login(userData.email, userData.password);
      setUser(loginData);
      // Legacy auto-seeding removed per user request
      
      return newUser;
    } catch (error) {
      throw error;
    }
  };

  const loginWithTokens = async (tokens, userInfo) => {
    const userData = {
      user_id: userInfo?.sub || `oauth_${Date.now()}`,
      email: userInfo?.email || '',
      name: userInfo?.name || '',
      provider: userInfo?.provider || 'federated',
      tokens: {
        idToken: tokens.idToken,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      },
    };
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('idToken', tokens.idToken);
    localStorage.setItem('accessToken', tokens.accessToken);
    if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loginWithTokens,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
